"use client";

import React, { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { AlertTriangle, CheckCircle, Play, Download, FileText, Search, Upload, ArrowLeft, Loader2, MessageSquare, Save } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";

interface InvExceptionTestingProps {
  onBack?: () => void;
}

interface ExceptionItem {
  id: number;
  description: string;
  selected: boolean;
}

const EXCEPTIONS: ExceptionItem[] = [
  { id: 1, description: "Items in stock with no quantity but values", selected: false },
  { id: 2, description: "Items in stock with no value but quantity", selected: false },
  { id: 3, description: "Items in stock with negative value", selected: false },
  { id: 4, description: "Items with GRN date blank", selected: false },
  { id: 5, description: "Items with Manufacturing date blank", selected: false },
  { id: 6, description: "Items with Manufacturing date after GRN date", selected: false },
];

export default function InvExceptionTesting({ onBack }: InvExceptionTestingProps) {
  const [exceptions, setExceptions] = useState<ExceptionItem[]>(EXCEPTIONS);
  const [selectedIRFile, setSelectedIRFile] = useState<string>("");
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [fileUrl, setFileUrl] = useState<string>("");
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `INVENTORY_EXCEPTION_TESTING_${normalizedSection}`;
  };
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "completed" | "error">("idle");
  
  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Inventory", "Exception Testing");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_INV_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_INV_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "EXCEPTION-TESTING-INVENTORY-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  useEffect(() => {
    // On mount, mimic pressing Search for Files and Load Exceptions
    searchForFiles();
    loadFileUsageReference();
    (async () => {
      try {
        if (window.sharePointAPI?.loadExceptionList) {
          const result = await window.sharePointAPI.loadExceptionList("inventory");
          if (result?.success && Array.isArray(result.data)) {
            setExceptions(prev => prev.map(exp => ({ ...exp, selected: (result.data as number[]).includes(exp.id) })));
          }
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_INV_IR");
        if (result.success && result.data?.files) {
          const files = result.data.files.map((f: any) => f.name).filter((n: string) => n && n.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const reader = new FileReader();
            const base64Content: string = await new Promise((resolve, reject) => {
              reader.onload = () => resolve((reader.result as string).split(',')[1]);
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });
            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: getFY_YEAR(),
                sectionList: ["Execution_INV_IR"],
              });
              if (result.success) {
                setSelectedIRFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else setUploadStatus("error");
            } else setUploadStatus("error");
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const handleToggle = (id: number) => setExceptions(prev => prev.map(e => e.id === id ? { ...e, selected: !e.selected } : e));
  const toggleAll = () => {
    const all = exceptions.every(e => e.selected);
    setExceptions(prev => prev.map(e => ({ ...e, selected: !all })));
  };

  const handleSaveExceptions = async () => {
    const selectedExceptionNumbers = exceptions
      .filter(exp => exp.selected)
      .map(exp => exp.id);
    
    if (selectedExceptionNumbers.length === 0) {
      alert("Please select at least one exception to save");
      return;
    }

    setSaveStatus("saving");
    try {
      if (!window.sharePointAPI?.saveExceptionList) {
        throw new Error("SharePoint API method 'saveExceptionList' is not available. Please restart the application.");
      }

      const result = await window.sharePointAPI.saveExceptionList(selectedExceptionNumbers, "inventory");

      if (result.success) {
        setSaveStatus("completed");
        alert(`Successfully saved ${selectedExceptionNumbers.length} exception(s) to Exception_Test.json`);
        setTimeout(() => setSaveStatus("idle"), 3000);
      } else {
        setSaveStatus("error");
        alert(`Failed to save exceptions: ${result.error || "Unknown error"}`);
      }
    } catch (error) {
      setSaveStatus("error");
      console.error("Error saving exceptions:", error);
      alert(`Failed to save exceptions: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleLoadExceptions = async () => {
    try {
      if (!window.sharePointAPI?.loadExceptionList) {
        alert("SharePoint API method 'loadExceptionList' is not available. Please restart the application.");
        return;
      }
      const result = await window.sharePointAPI.loadExceptionList("inventory");
      if (result.success) {
        const selectedIds = Array.isArray(result.data) ? result.data : [];
        setExceptions(prev => prev.map(exp => ({ ...exp, selected: selectedIds.includes(exp.id) })));
        alert(`Loaded ${selectedIds.length} exception(s) from Exception_Test.json`);
      } else {
        alert(`Failed to load exceptions: ${result.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error loading exceptions:", error);
      alert(`Failed to load exceptions: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const canRun = () => selectedIRFile && exceptions.some(e => e.selected);

  const run = async () => {
    if (!canRun()) return;
    
    // Save file name to file usage reference
    const entries: FileUsageEntry[] = [];
    if (selectedIRFile) {
      const fileName = selectedIRFile.includes(' (') ? selectedIRFile.split(' (')[0] : selectedIRFile;
      entries.push({ "Search for files": fileName });
    }
    if (entries.length > 0) {
      await updateProcedureFileUsageForWorkspace("Inventory", "Exception Testing", entries);
      await loadFileUsageReference();
    }
    
    // Trigger the same functionality as "Save Exceptions" before running tests
    try {
      await handleSaveExceptions();
    } catch (e) {
      console.warn("Saving exceptions before run failed, continuing with run", e);
    }
    
    setProcessingStatus("running");
    try {
      const expn_no = exceptions.filter(e => e.selected).map(e => e.id);
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_inv_exception_testing", {
          inputFiles: [],
          options: { expn_no },
        });
        if (result.ok) {
          setProcessingStatus("completed");
        } else {
          setProcessingStatus("error");
        }
      } else setProcessingStatus("error");
    } catch {
      setProcessingStatus("error");
    }
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "INV";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "EXCEPTION-TESTING-INVENTORY-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Inventory - Exception Testing</h2>
          <p className="text-gray-400">Automated exception checks for inventory register</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* File Search Status */}
          <div className="flex items-center gap-3">
            {fileSearchStatus === "searching" && (
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching for files...</span>
              </div>
            )}
            {fileSearchStatus === "found" && (<span className="text-green-400 font-bold"> FOUND!</span>)}
            {fileSearchStatus === "not_found" && (<span className="text-yellow-400">No files found</span>)}
          </div>
          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select Inventory Register</Label>
                <Select value={selectedIRFile} onValueChange={setSelectedIRFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundFiles.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Search for files"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["Search for files"])?.["Search for files"]}
                  </p>
                )}
              </div>
            </div>
          )}
          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input value={referenceValue} onChange={(e) => setReferenceValue(e.target.value)} placeholder="Enter reference value" className="border-white/10 bg-black/40 text-white" />
                </div>
                <Button onClick={uploadFile} disabled={uploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {uploadStatus === "uploading" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>) : (<><Upload className="h-4 w-4 mr-2" /> Upload File</>)}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5" />
              Exception Tests
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Exception Tests"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={toggleAll} className="border-white/10">
                {exceptions.every(e => e.selected) ? "Deselect All" : "Select All"}
              </Button>
              <span className="rounded bg-blue-500/20 px-2 py-1 text-xs text-blue-300">{exceptions.filter(e => e.selected).length} selected</span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded border border-white/10 bg-white/5">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20 text-white">Sl. No.</TableHead>
                  <TableHead className="text-white">Exception</TableHead>
                  <TableHead className="w-40 text-white">Selection</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exceptions.map((ex) => (
                  <TableRow key={ex.id}>
                    <TableCell className="text-white">{ex.id}</TableCell>
                    <TableCell className="text-white">{ex.description}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Checkbox id={`exception-${ex.id}`} checked={ex.selected} onCheckedChange={() => handleToggle(ex.id)} />
                        <Label htmlFor={`exception-${ex.id}`} className="text-white">{ex.selected ? "Selected" : "Select"}</Label>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5" />
              Execute Exception Tests
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Execute Exception Tests"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Run selected exception tests on the inventory register</p>
              {!canRun() && (<p className="mt-1 text-xs text-red-400">Please select at least one exception test and choose the inventory register file</p>)}
            </div>
            <Button onClick={run} disabled={!canRun() || processingStatus === "running"} className="bg-red-600 hover:bg-red-700 disabled:opacity-50">
              {processingStatus === "running" ? (<><div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> Running Tests...</>) : (<><Play className="mr-2 h-4 w-4" /> Run Exception Tests</>)}
            </Button>
          </div>
          {processingStatus === "running" && (
            <div className="flex items-center gap-2 text-blue-400">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-400 border-t-transparent" />
              <span>Processing Exception Tests...</span>
            </div>
          )}
          {processingStatus === "completed" && (
            <div className="flex gap-2">
              <Button variant="outline" className="border-green-500/30 text-green-300" onClick={() => { if (fileUrl) window.open(fileUrl, "_blank"); }} disabled={!fileUrl}>
                <FileText className="mr-2 h-4 w-4" />
                Open Exception Testing
              </Button>
            </div>
          )}
          {processingStatus === "error" && (
            <div className="rounded border border-red-500/20 bg-red-500/10 p-4">
              <div className="flex items-center gap-3"><AlertTriangle className="h-5 w-5 text-red-500" /><div><h4 className="font-medium text-red-300">Exception Testing Failed</h4><p className="text-sm text-red-200">An error occurred while running the exception tests. Please check your data and try again.</p></div></div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="inventory"
            itemId="EXCEPTION-TESTING-INVENTORY-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Exception Tests")]: "Exception Tests",
              [getSectionQuestionId("Execute Exception Tests")]: "Execute Exception Tests",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}


